import { AfterViewChecked, ChangeDetectorRef, Component, HostBinding, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { LayoutService } from '@auto-editor/data-access-layout';
import { LocalStorageKey, LocalStorageService } from '@auto-editor/data-access-local-storage';
import { UserService } from '@auto-editor/data-access-user';
import { environment } from '@auto-editor/environment';
import { AuthService } from '@auto-editor/feature-auth';
import { BasicCustomerNameDto } from '@auto-editor/shared/models';
import { DataQuestionnaireService, UtilsService } from '@auto-editor/shared/services';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable, startWith } from 'rxjs';

@Component({
  selector: 'auto-editor-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrl: './toolbar.component.scss',
  standalone: false
})
export class ToolbarComponent implements OnInit {
  @Input()
  @HostBinding('class.shadow-b')
  hasShadow!: boolean;

  menus = [
    {
      title: '',
      value: 'editor/edit',
      disabled: false
    },
    {
      title: '',
      value: 'share',
      disabled: false
    }
  ];

  arrayLanguages = [
    {
      asset: 'es',
      label: 'es',
      value: 'es'
    },
    {
      asset: 'fr',
      label: 'fr',
      value: 'fr'
    },
    {
      asset: 'en',
      label: 'en',
      value: 'en'
    }
  ];

  selectedMenu = 0;
  showButtonGroup: boolean;
  isDesktop$ = this.layoutService.isDesktop$;
  duplicatedName: boolean;

  selectLanguagesForm: FormControl;

  qnId: string;

  showSelectorClients: boolean;
  clientList: BasicCustomerNameDto[] = [];

  customer = new FormControl();
  filteredOptions: Observable<BasicCustomerNameDto[]>;

  constructor(
    private layoutService: LayoutService,
    private router: Router,
    private route: ActivatedRoute,
    public utilsService: UtilsService,
    private dataQuestionnaireService: DataQuestionnaireService,
    private authService: AuthService,
    private userService: UserService,
    private localStorage: LocalStorageService,
    private translateService: TranslateService
  ) {
    this.qnId = this.route.snapshot.paramMap.get('qnId');

    this.router.events.subscribe((changes) => {
      if (changes instanceof NavigationEnd) {
        this.showButtonGroup = !(changes.url === '/' || changes.url === '/dashboard');
      }
      this.evaluateUrl();
    });
    this.utilsService.questionnaireChanged.subscribe(() => {
      if (this.utilsService.isOldVersion()) this.menus.find((menu) => menu.value === 'share').disabled = true;
    });
  }

  ngOnInit(): void {
    this.translations();
    this.getData();
    this.filteredCustomerOptions();
    this.selectLanguagesForm = new FormControl(this.getLanguageKey());
  }

  translations() {
    this.menus[0].title = this.translateService.instant('toolbar.edit');
    this.menus[1].title = this.translateService.instant('toolbar.share');
    this.arrayLanguages.forEach((lang) => {
      lang.label = this.translateService.instant(`languages.${lang.asset}`);
    });
    this.translateService.onLangChange.subscribe(() => {
      this.menus[0].title = this.translateService.instant('toolbar.edit');
      this.menus[1].title = this.translateService.instant('toolbar.share');
      this.arrayLanguages.forEach((lang) => {
        lang.label = this.translateService.instant(`languages.${lang.asset}`);
      });
    });
  }

  changeLanguage(event: string) {
    this.localStorage.saveData(LocalStorageKey.languageSelected, event);
    this.translateService.use(event);
  }

  getLanguageKey() {
    return this.localStorage.getData(LocalStorageKey.languageSelected);
  }

  private _filter(value: string): BasicCustomerNameDto[] {
    const filterValue = value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

    return this.clientList.filter((option) =>
      option.name.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').includes(filterValue)
    );
  }

  displayFn(customer: BasicCustomerNameDto): string {
    return customer && customer.name ? customer.name : '';
  }

  customerSelected(id: string): void {
    this.localStorage.saveData(LocalStorageKey.clientSelected, JSON.stringify(id));
    this.dataQuestionnaireService.customerSelected.next(id);
  }

  goToDashboard() {
    this.router.navigateByUrl('/dashboard');
  }

  evaluateUrl() {
    this.qnId = this.utilsService.questionnaire?.id;
    let actualLink = this.router.url.split('/');

    if (actualLink.includes('editor')) this.selectedMenu = 0;
    else if (actualLink.includes('share')) this.selectedMenu = 1;
    else this.selectedMenu = -1;
  }

  changeMenu(type: string, i: number) {
    this.selectedMenu = i;

    this.router.navigateByUrl(`questionnaire/${this.localStorage.getData(LocalStorageKey.qnSelected)}/${type}`);
  }

  renameQn(newName: string) {
    this.dataQuestionnaireService.renameQuestionnaire(newName, this.utilsService.questionnaire.id).subscribe({
      next: () => {
        this.utilsService.questionnaire.name = newName;
      },
      error: () => {
        this.duplicatedName = true;
      }
    });
  }

  logout(): void {
    this.authService.logout().subscribe(() => {
      this.userService.logOut();
      const urlLogOut =
        environment.logOut +
        `?response_type=code&client_id=${environment.apiClientId}&scope=${environment.scope}&redirect_uri=` +
        encodeURIComponent(window.location.origin + '/auth');
      window.location.href = urlLogOut;
    });
  }

  canSelectCustomer(): boolean {
    if (this.userService.userLoggedIn()) {
      if (this.userService.userInfo?.authorities?.includes('access_all')) {
        return true;
      } else {
        return false;
      }
    }
  }

  getData(): void {
    this.dataQuestionnaireService.getClientList().subscribe({
      next: (data: BasicCustomerNameDto[]) => {
        this.clientList = data.sort((a, b) => a.name.localeCompare(b.name));
        this.setInLocalStorageUserCustomer();
        this.setInFormCurrentCustomer();
        this.showSelectorClients = this.canSelectCustomer();
      },
      error: () => {
        console.error(this.translateService.instant('toolbar.error-get'));
      }
    });
  }

  filteredCustomerOptions(): void {
    this.filteredOptions = this.customer.valueChanges.pipe(
      startWith(),
      map((value) => {
        const name = typeof value === 'string' ? value : value?.name;
        return name ? this._filter(name as string) : this.clientList.slice();
      })
    );
  }

  setInLocalStorageUserCustomer(): void {
    this.clientList.filter((client) => {
      if (client.id === this.userService.userInfo.customerId) {
        if (!this.localStorage.getData(LocalStorageKey.clientSelected)) {
          this.localStorage.saveData(LocalStorageKey.clientSelected, JSON.stringify(client.id));
        }
      }
    });
  }

  setInFormCurrentCustomer(): void {
    if (JSON.parse(this.localStorage.getData(LocalStorageKey.clientSelected))) {
      this.customer.setValue(this.clientList.find((client) => client.id === JSON.parse(this.localStorage.getData(LocalStorageKey.clientSelected))));
    }
  }
}
